import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, CheckCircle, AlertTriangle, Clock, Receipt, HardDriveDownload, Banknote } from 'lucide-react';
import api from '../api';
import { Skeleton } from '../components/Loading';
import { can } from '../auth';

// "3 days ago" reads faster than a date when you're scanning for trouble
function daysAgoLabel(iso) {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const STATUS_COLORS = {
  created: 'bg-gray-100 text-gray-700',
  collected: 'bg-blue-100 text-blue-700',
  at_depot: 'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700',
  failed_delivery: 'bg-red-100 text-red-700',
  returned_to_depot: 'bg-orange-100 text-orange-700',
  rescheduled: 'bg-indigo-100 text-indigo-700',
};

const STATUS_LABELS = {
  created: 'Created',
  collected: 'Collected',
  at_depot: 'At Depot',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  failed_delivery: 'Failed Delivery',
  returned_to_depot: 'Returned to Depot',
  rescheduled: 'Rescheduled',
};

export default function Dashboard() {
  const isAdmin = can('system.settings');   // the backup tile
  const qc = useQueryClient();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['summary'],
    queryFn: () => api.get('/reports/summary').then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: backup } = useQuery({
    queryKey: ['backup-health'],
    queryFn: () => api.get('/settings/backup/health').then((r) => r.data),
    enabled: isAdmin,          // endpoint is admin-only
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Emails that came back undeliverable — the office must re-send or fix the address
  const { data: bounces = [] } = useQuery({
    queryKey: ['email-bounces'],
    queryFn: () => api.get('/email/bounces', { params: { days: 30 } }).then((r) => r.data).catch(() => []),
    refetchInterval: 5 * 60 * 1000,
  });
  const dismissBounce = async (b) => {
    try { await api.post(`/email/bounces/${b.id}/seen`); qc.invalidateQueries({ queryKey: ['email-bounces'] }); } catch { /* ignore */ }
  };
  const { data: unresolved = [] } = useQuery({
    queryKey: ['unresolved'],
    queryFn: () => api.get('/parcels/unresolved').then((r) => r.data),
    refetchInterval: 30000,
  });

  // Past days that took money but were never cashed up (staff-only endpoint)
  const { data: missedCashups = [] } = useQuery({
    queryKey: ['cashup-missing'],
    queryFn: () => api.get('/billing/cashup/missing').then((r) => r.data),
    enabled: can('billing.payments'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Cash-ups that were done but didn't balance (over/short)
  const { data: unbalancedCashups = [] } = useQuery({
    queryKey: ['cashup-unbalanced'],
    queryFn: () => api.get('/billing/cashup/unbalanced').then((r) => r.data),
    enabled: can('billing.payments'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const stats = [
    { label: 'Unresolved', value: summary?.unresolved ?? '—', icon: Clock, color: 'text-yellow-500 bg-yellow-50' },
    { label: 'Delivered (this week)', value: summary?.delivered_week ?? '—', icon: CheckCircle, color: 'text-green-500 bg-green-50' },
    { label: 'Failed Deliveries', value: summary?.failed_total ?? '—', icon: AlertTriangle, color: 'text-red-500 bg-red-50' },
    { label: 'Created (this week)', value: summary?.created_week ?? '—', icon: Package, color: 'text-blue-500 bg-blue-50' },
    {
      label: 'Ready to invoice', value: summary?.ready_to_invoice ?? '—', icon: Receipt,
      color: parseInt(summary?.ready_to_invoice) > 0 ? 'text-orange-600 bg-orange-50' : 'text-green-500 bg-green-50',
      to: '/billing?tab=ready',
      title: 'Delivered and priced, but not on an invoice yet — money waiting to be billed',
    },
    {
      label: 'Needs a price', value: summary?.unbilled ?? '—', icon: AlertTriangle,
      color: parseInt(summary?.unbilled) > 0 ? 'text-amber-600 bg-amber-50' : 'text-green-500 bg-green-50',
      to: '/billing?tab=unbilled',
      title: 'Parcels with no price yet — they cannot be invoiced until priced',
    },
  ];

  if (isAdmin) {
    stats.push({
      label: backup
        ? `Last backup${backup.last_success_at ? ' · ' + new Date(backup.last_success_at).toLocaleDateString('en-ZA') : ''}`
        : 'Last backup',
      value: backup ? daysAgoLabel(backup.last_success_at) : '—',
      icon: HardDriveDownload,
      color: !backup ? 'text-gray-400 bg-gray-50'
        : backup.healthy ? 'text-green-500 bg-green-50'
        : backup.severity === 'error' ? 'text-red-500 bg-red-50' : 'text-amber-600 bg-amber-50',
      to: '/settings?tab=database',
      title: backup?.problem || 'Backups are running normally',
      small: true,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Dashboard</h1>
        <Link to="/parcels/new" className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
          + New Parcel
        </Link>
      </div>

      {/* Stats */}
      {/* Wraps to rows of four — seven tiles squeezed onto one row are unreadable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color, to, title, small }) => {
          const card = (
            <div className={`bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 h-full ${to ? 'hover:shadow-md transition-shadow' : ''}`}>
              <div className={`p-2 rounded-lg ${color}`}><Icon size={20} /></div>
              <div className="min-w-0">
                {/* A dash reads as "nothing to report"; a skeleton reads as
                    "not in yet". They are different answers and the tiles
                    should not blur them. */}
                {summaryLoading && value === '—'
                  ? <Skeleton className={small ? 'h-6 w-16 mb-1' : 'h-8 w-16 mb-1'} />
                  : <p className={`font-bold text-gray-800 ${small ? 'text-lg' : 'text-2xl'}`}>{value}</p>}
                <p className="text-xs text-gray-500 truncate">{label}</p>
              </div>
            </div>
          );
          return to
            ? <Link key={label} to={to} title={title || 'Open the unbilled list on the Billing page'}>{card}</Link>
            : <div key={label}>{card}</div>;
        })}
      </div>

      {/* Days that took money but were never cashed up */}
      {missedCashups.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200">
          <div className="p-4 border-b border-amber-100 flex items-center gap-2">
            <Banknote size={18} className="text-amber-600" />
            <h2 className="font-semibold text-gray-700">
              Cash-ups not done <span className="text-amber-600">({missedCashups.length})</span>
            </h2>
            <span className="text-xs text-gray-400 ml-1">— days money came in but the till was never balanced</span>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {missedCashups.map((d) => {
              const date = d.date.split('T')[0];
              return (
                <Link key={date} to={`/cash-up?date=${date}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-amber-50">
                  <span className="text-sm font-medium text-gray-700">
                    {new Date(d.date).toLocaleDateString('en-ZA', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-gray-500">{d.payment_count} payment{Number(d.payment_count) === 1 ? '' : 's'}</span>
                    <span className="text-gray-500">cash R{parseFloat(d.cash).toFixed(2)}</span>
                    <span className="text-orange-600 font-semibold">Cash up →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Emails that bounced */}
      {bounces.some((b) => b.matched_kind) && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200">
          <div className="p-4 border-b border-red-100 flex items-center gap-2 flex-wrap">
            <AlertTriangle size={18} className="text-red-500" />
            <h2 className="font-semibold text-gray-700">
              Emails that bounced <span className="text-red-600">({bounces.filter((b) => b.matched_kind).length})</span>
            </h2>
            <span className="text-xs text-gray-400 ml-1">— the address rejected them; check the client&apos;s email and send again</span>
            {bounces.some((b) => !b.matched_kind) && (
              <span className="text-xs text-gray-400 ml-auto">+ {bounces.filter((b) => !b.matched_kind).length} other bounce notices in the mailbox, not from Expressway</span>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {bounces.filter((b) => b.matched_kind).map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-red-50 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-gray-800">{b.failed_address || 'unknown address'}</span>
                  {b.matched_kind === 'invoice' && b.matched_ref && <Link to={`/invoices?client=${b.client_id}`} className="ml-2 text-orange-600 hover:underline">invoice {b.matched_ref}{b.client_name ? ` · ${b.client_name}` : ''}</Link>}
                  {b.matched_kind === 'statement' && <Link to={`/clients/${b.client_id}`} className="ml-2 text-orange-600 hover:underline">statement · {b.matched_ref}</Link>}
                  {b.matched_kind === 'notification' && b.matched_ref && <Link to={`/parcels/${b.matched_ref}`} className="ml-2 text-orange-600 hover:underline">parcel {b.matched_ref}</Link>}
                  {!b.matched_kind && <span className="ml-2 text-gray-400 text-xs">not one of this program&apos;s emails — {b.subject}</span>}
                  <span className="block text-xs text-gray-400">{new Date(b.received_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                <button onClick={() => dismissBounce(b)} className="text-xs text-gray-400 hover:text-gray-700 underline">dismiss</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cash-ups that were done but didn't balance */}
      {unbalancedCashups.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200">
          <div className="p-4 border-b border-red-100 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" />
            <h2 className="font-semibold text-gray-700">
              Cash-ups that don&apos;t balance <span className="text-red-600">({unbalancedCashups.length})</span>
            </h2>
            <span className="text-xs text-gray-400 ml-1">— the counted cash didn&apos;t match what was expected</span>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {unbalancedCashups.map((d) => {
              const date = d.business_date.split('T')[0];
              const v = parseFloat(d.variance);
              return (
                <Link key={date} to={`/cash-up?date=${date}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-red-50">
                  <span className="text-sm font-medium text-gray-700">
                    {new Date(d.business_date).toLocaleDateString('en-ZA', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-gray-500">counted R{parseFloat(d.counted_cash).toFixed(2)}</span>
                    <span className={`font-semibold ${v < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                      {v < 0 ? `SHORT R${Math.abs(v).toFixed(2)}` : `OVER R${v.toFixed(2)}`}
                    </span>
                    <span className="text-orange-600 font-semibold">Review →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Unresolved parcels */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">Unresolved Parcels</h2>
          <Link to="/parcels" className="text-sm text-orange-500 hover:underline">View all</Link>
        </div>
        <div className="divide-y">
          {unresolved.length === 0 && (
            <p className="p-6 text-center text-gray-400 text-sm">All parcels delivered!</p>
          )}
          {unresolved.map((p) => (
            <Link
              key={p.tracking_number}
              to={`/parcels/${p.tracking_number}`}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div>
                <p className="font-mono text-sm font-semibold text-gray-800">{p.tracking_number}</p>
                <p className="text-xs text-gray-500">{p.recipient_name} {p.recipient_phone ? `· ${p.recipient_phone}` : ''}</p>
              </div>
              <div className="text-right">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[p.status]}`}>
                  {STATUS_LABELS[p.status]}
                </span>
                <p className="text-xs text-gray-400 mt-1">{new Date(p.updated_at).toLocaleDateString('en-ZA')}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
